<template>
    <div class="col-sm-12 nopads">
        <h4 class="form-header">{{ $t('contracts.external.daily_trips')}}</h4>
        <span class="tip-light">{{ $t('contracts.external.trip_search_hint')}}</span>
        <div class="row">
            <div class="col-sm-6 pl-1 pr-1">
                <contract-order-multiselect
                    ref="orderFilter"
                    :contract-ids="contractOptions"
                    :single-mode="true"
                    :only-ely="isMaterialReport"
                    @ordersSelected="setOrder"
                />
            </div>
            <div class="col-sm-2 nopads pl-1 pr-1">
                <span class="span-title">{{ $t('trip_list.search_with_road_num') }}</span>
                <input
                    id="roadInput"
                    v-model="roadNumber"
                    type="number"
                    class="form-control form-control-sm no-margin"
                    :placeholder="$t('trip_list.search_with_road_num')"
                />

            </div>
            <div class="col-sm-2 nopads pl-1 pr-1">
                <span class="span-title">{{ $t('trip_list.search_with_road_sec') }}</span>
                <input
                    id="roadSecFrom"
                    v-model="roadSecFrom"
                    type="number"
                    class="form-control form-control-sm no-margin"
                    :placeholder="$t('trip_list.road_sec_from')"
                />
            </div>
            <div class="col-sm-2 nopads">
                <span class="span-title">{{ $t('trip_list.search_with_road_sec') }}</span>
                <input
                    id="roadSecTo"
                    v-model="roadSecTo"
                    type="number"
                    class="form-control form-control-sm no-margin"
                    :placeholder="$t('trip_list.road_sec_to')"
                />
            </div>
        </div>
        <div class="col-12 d-flex justify-content-end">
            <button class="btn btn-primary form-button" :disabled="!order" @click.stop="searchTrips()">
                {{ $t('ins_report.search') }}
            </button>
        </div>
        <span v-if="results.length > 0" class="tip-light">{{ $t('contracts.external.trip_select_hint')}}</span>
        <div v-if="results.length > 0" class="list-content">
            <vue3-datatable
                ref="resultTable"
                :rows="results"
                :columns="fields"
                :pagination="false"
                :sticky-header="true"
                :height="results.length > 5 ? '30vh' : '100%'"
                :sortable="true"
                has-checkbox
                skin="bh-table-compact"
                row-class="routa-table-row"
                cell-class="routa-table-cell"
                @row-select="rowSelected"
            >
                <template #mode="row">
                    {{ getTripModeString(row.value.mode) }}
                </template>
                <template #start_time="row">
                    {{ toLocalTime(row.value.start_time) }}
                </template>
                <template #end_time="row">
                    {{ toLocalTime(row.value.end_time) }}
                </template>
                <template #map="row">
                    <font-awesome-icon
                        :id="'coord_'+row.value.id"
                        icon="map"
                        class="row-icon"
                        style="color: #4AAE9B"
                        @click.stop="showTripOnMap(row.value.id)"
                    />
                </template>
            </vue3-datatable>
        </div>
        <div class="row mt-3">
            <div class="col-sm-6">
                    <div v-if="!isMaterialReport && selectedTrips.length > 0">
                        <span class="span-title">{{ $t('contracts.corrected_order')}}</span>
                        <contract-order-multiselect
                            ref="orderFilter"
                            :contract-ids="contract ?  [contract.id] : []"
                            :single-mode="true"
                            :pre-selected-order="preSelectedOrder ? preSelectedOrder.id : undefined"
                            @orders-selected="setCorrectedOrder"
                        />
                    </div>
                <div v-if="!isMaterialReport && selectedTrips.length > 0" class="row">
                     <span class="hint-text">{{ $t('contracts.corrected_order_hint')}}</span>
                </div>
            </div>
            <div class="col-sm-6 d-flex justify-content-end align-items-end">
                <button v-if="isMaterialReport" class="btn btn-secondary form-button" @click.stop="createRouteWithMaterials">
                    {{ $t('contracts.external.route_reports_no_route') }}
                </button>
                <button class="btn btn-info form-button" :disabled="selectedTrips.length < 1" @click.stop="actionButtonClick">
                    {{ buttonText }}
                </button>
            </div>
        </div>
        <contract-modal
            v-if="mapVisible"
            ref="contractModal"
            @close="mapVisible = false"
            :selected-report-type="selectedReportType"
            :map-visible="mapVisible"
            :map-trip-id="mapTripId"
            :results="results"
        />
        <base-modal v-if="createRoute" hide-header hide-footer no-padding size="xl">
            <route-material-creator @close="createRoute = false" :contract="contract" />
        </base-modal>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {restApi} from '../../mixins/RestApiMixin'
import {workManagementHelper} from '../../mixins/WorkManagementMixin'
import {userHelper} from "@/components/mixins/UserMixin";
import {timeUtils} from "@/components/mixins/TimeUtils";
import {vehicleHelper} from "@/components/mixins/VehicleMixin";
import ContractModal from "@/components/contract/external/ContractModal.vue";
import {contractHelper, contractReportTypes} from "@/components/mixins/ContractMixin";
import RouteMaterialCreator from "@/components/harja/RouteMaterialCreator"
import Vue3Datatable from "@bhplugin/vue3-datatable";
import BaseModal from "@/components/BaseModal";
import ContractOrderMultiselect from "../../order/ContractOrderMultiselect.vue";

export default {
    name: 'ContractDailyTrips',
    components: {ContractOrderMultiselect, BaseModal, ContractModal,RouteMaterialCreator, Vue3Datatable},
    mixins: [workManagementHelper, restApi, userHelper, timeUtils, vehicleHelper, contractHelper],
    props: {
        contract: {
            type: Object,
            default: null
        },
        date: {
            type: Number,
            default: new Date().getTime()
        },
        withGeom: {
            type: Boolean,
            default: false
        },
        selectedReportType: {
            type: Number,
            default: null
        },
        buttonText: {
            type: String,
            default() {
                return this.$t('contracts.external.create_report')
            }
        }
    },
    data: function () {
        return {
            loading: false,
            createRoute: false,
            order: null,
            roadNumber: null,
            roadSecFrom: null,
            roadSecTo: null,
            results: [],
            selectedTrips: [],
            mapTripId: null,
            mapVisible: false,
            map: null,
            mapTrip: null,
            fields:  [
                { field: 'company_name', title: this.$t('users.company'), sort: true, headerClass: 'routa-table-header'},
                { field: 'driver', title: this.$t('trip_list.driver'), sort: true, headerClass: 'routa-table-header'},
                { field: 'vehicle', title: this.$t('trip_list.vehicle'), sort: true, headerClass: 'routa-table-header'},
                { field: 'mode', title: this.$t('trip_list.mode'), sort: true, headerClass: 'routa-table-header'},
                { field: 'order', title: this.$t('vehicle_position.order'), sort: true, headerClass: 'routa-table-header'},
                { field: 'start_time', title: this.$t('trip_list.start_time'), sort: true, headerClass: 'routa-table-header'},
                { field: 'start_road', title: this.$t('trip_list.start_loc'), sort: true, headerClass: 'routa-table-header'},
                { field: 'end_time', title: this.$t('trip_list.end_time'), sort: true, headerClass: 'routa-table-header'},
                { field: 'end_road', title: this.$t('trip_list.end_loc'), sort: true, headerClass: 'routa-table-header'},
                { field: 'map', title: this.$t('menu.map'), sort: false, headerClass: 'routa-table-header'}
            ],
            correctedOrder: null,
            contractOptions: []
        }
    },
    computed: {
        isMaterialReport() {
            return this.selectedReportType === contractReportTypes.REPORT_MATERIAL
        },
        selectMode() {
            if (this.isMaterialReport) {
                return 'single'
            } else {
                return 'multi'
            }
        }
    },

    watch: {
        mapVisible: function (val) {
            if (!val) {
                this.mapTrip = null
                this.mapTripId = null
            }
            if (this.$refs.contractModal) {
                this.$refs.contractModal.modalVisible = val
            }
        }
    },
    created() {
        this.contractOptions = [this.contract.id]
    },
    methods: {
        setOrder(order) {
            this.order = order
        },
        setCorrectedOrder(order) {
            this.correctedOrder = order
        },
        actionButtonClick() {
            if (this.isMaterialReport) {
                this.showTripOnMap(this.selectedTrips[0].id)
            } else {
                this.$emit('createReports', this.getParams(), this.getSelectedTrips())
            }
        },
        createRouteWithMaterials() {
            this.createRoute = true
        },
        resetSelection() {
            this.selectedTrips = []
            this.results = []
        },
        hasSelectedTrips() {
            this.selectedTrips.length > 0
        },
        getSelectedTrips() {
           let results = []
           this.selectedTrips.forEach(trip => {
               const item = {
                   vehicle_id: trip['vehicle_id'],
                   driver_id: trip['driver_id'],
                   start_time: trip['start_time'],
                   end_time: trip['end_time'],
                   order_id: trip['order_id'],
                   corrected_order: this.correctedOrder?.id
               }
               if (this.withGeom && trip.geometry.length > 0) {
                   const startPoint = trip.geometry[0]
                   const endPoint = trip.geometry[trip.geometry.length-1]
                   item.start_lon = startPoint.x
                   item.start_lat = startPoint.y
                   item.end_lon = endPoint.x
                   item.end_lat = endPoint.y
               }
               results.push(JSON.stringify(item))
           })
           return results
        },
        getParams(withGeom = 0) {
            let params = {
                contract: this.contract.id,
                customer: this.contract.customer.id,
                contractor: this.contract.contractor.id,
                from: this.getStartOfTheDay(this.date).toISOString(),
                to: this.getEndOfTheDay(this.date).toISOString(),
                correctedOrder: this.correctedOrder?.id,
                with_geom: this.withGeom ? 1 : withGeom
            }
            if (this.order) {
                params.order = this.order.id
            }
            if (this.roadNumber > 0) {
                params.road_num = this.roadNumber
            }
            if (this.roadSecFrom > 0) {
                params.road_sec_from = this.roadSecFrom
            }
            if (this.roadSecTo > 0) {
                params.road_sec_to = this.roadSecTo
            }
            return params
        },
        searchTrips(withGeom = 0) {
            this.loading = true
            this.results = []
            this.selectedTrips = []
            this.mapTrip = null
            this.mapVisible = false
            this.restFetchParams(this.tripUrl, this.getParams(withGeom), this.handleTrips)
        },

        handleTrips(response) {
            this.loading = false
            if (response && response.data) {
                this.results = response.data
                if (this.mapTripId){
                    this.mapVisible = true
                }
            }
            // TODO - Error handling
        },

        rowSelected: function (items) {
            this.selectedTrips = items
        },

        showTripOnMap(tripId) {
            this.mapTripId = tripId
            const tripsHaveGeometry = this.results.some((trip) => trip.geometry);
            if (!tripsHaveGeometry) {
                this.searchTrips(1);
            } else {
                this.mapVisible = true
            }
        },

    }
}
</script>
<style scoped>

</style>
